import type { Event, Invoice, RSVP } from '@/types';
import { getRsvpExpectedAmount, isInviteeCoveredByInviter } from '@/lib/event-utils';
import { computeInvoiceBaseAmountForPayer } from '@/lib/ensure-invoice-for-rsvp';
import { getHeadlinePayableDueForDashboard, orderRsvpsForInvoice } from '@/lib/invoice-payment-breakdown';
import { getAmountReservedOnInvoice, INVOICE_AMOUNT_EPS } from '@/lib/invoice-utils';

import { getInvoicePayerRsvp, getPaymentSourceRsvp } from '@/lib/invoice-payer-rsvp';

export type AttendanceExportAmounts = {
  total: number;
  outstanding: number;
};

export { getInvoicePayerRsvp, getPaymentSourceRsvp };

export function getAttendanceRsvpPaymentState(
  rsvp: RSVP,
  rsvpList: RSVP[],
  eventData: Event,
  invoices: Record<string, Invoice>,
): {
  displayPaid: boolean;
  pendingVerification: boolean;
  statusPaid: boolean;
  /** True when this row is an invitee whose host pays; show "Host Pays" instead of Paid/Unpaid/Pending Verification. */
  hostPaysSeat: boolean;
} {
  const payer = getInvoicePayerRsvp(rsvp, rsvpList);
  const sourceInvoice = payer.invoiceId ? invoices[payer.invoiceId] : undefined;
  const pendingVerification = sourceInvoice?.status === 'pending_verification';
  const zeroPaymentRequired = getRsvpExpectedAmount(payer, eventData) === 0;
  const statusPaid = sourceInvoice ? sourceInvoice.status === 'paid' : !!payer.paid;

  const ordered = sourceInvoice ? orderRsvpsForInvoice(sourceInvoice, rsvpList) : [];
  const breakdown =
    sourceInvoice && ordered.length > 0 ? { event: eventData, rsvps: rsvpList } : null;
  const liveDue = sourceInvoice
    ? getHeadlinePayableDueForDashboard(sourceInvoice, breakdown)
    : null;

  // liveDue uses current event prices (no costPerPerson / stored-line-price fallback).
  // Guard against false "paid" when live calc returns 0 but stored balance is still outstanding
  // (e.g. legacy costPerPerson events, or ticket types removed after invoicing).
  const storedBalanceClear =
    !sourceInvoice || Number(sourceInvoice.outstandingBalance) <= INVOICE_AMOUNT_EPS;
  const displayPaid =
    payer.paid ||
    (liveDue !== null && liveDue <= INVOICE_AMOUNT_EPS && storedBalanceClear) ||
    (sourceInvoice && sourceInvoice.totalAmount === 0) ||
    zeroPaymentRequired;

  return {
    displayPaid,
    pendingVerification,
    statusPaid,
    hostPaysSeat: isInviteeCoveredByInviter(rsvp),
  };
}

/**
 * Total and outstanding amounts for attendance exports; mirrors the Amount column on AttendanceListPage.
 */
export function getAttendanceExportAmounts(
  rsvp: RSVP,
  rsvpList: RSVP[],
  eventData: Event,
  invoices: Record<string, Invoice> = {},
): AttendanceExportAmounts | null {
  if (rsvp.apologies) {
    return null;
  }

  const payer = getInvoicePayerRsvp(rsvp, rsvpList);
  const inv = payer.invoiceId ? invoices[payer.invoiceId] : undefined;
  const paymentState = getAttendanceRsvpPaymentState(rsvp, rsvpList, eventData, invoices);

  let total: number;
  let outstanding: number;
  if (inv) {
    const ordered = orderRsvpsForInvoice(inv, rsvpList);
    const breakdown = ordered.length > 0 ? { event: eventData, rsvps: rsvpList } : null;
    // Reserved includes bank transfers awaiting verification, so the total doesn't drop to 0 while pending.
    const reserved = getAmountReservedOnInvoice(inv);
    outstanding = getHeadlinePayableDueForDashboard(inv, breakdown);
    total = reserved + outstanding;
  } else {
    total = computeInvoiceBaseAmountForPayer(payer, eventData, rsvpList);
    outstanding = paymentState.displayPaid ? 0 : total;
  }
  // Host-covered guests owe nothing themselves.
  if (paymentState.hostPaysSeat) {
    outstanding = 0;
  }
  return {
    total: Math.round(total * 100) / 100,
    outstanding: Math.round(outstanding * 100) / 100,
  };
}

/**
 * Payment column text for attendance PDF/CSV exports; aligns with {@link getAttendanceRsvpPaymentState} display rules.
 */
export function getAttendanceExportPaymentStatusLabel(rsvp: RSVP, eventData: Event): string {
  if (rsvp.apologies) {
    return 'Apology';
  }
  if (isInviteeCoveredByInviter(rsvp)) {
    return 'Host Pays';
  }
  const zeroPaymentRequired = getRsvpExpectedAmount(rsvp, eventData) === 0;
  if (rsvp.paid || zeroPaymentRequired) {
    return 'Paid';
  }
  return 'Unpaid';
}
